import React, { useEffect, useMemo, useState } from "react";
import {
  AutoAwesome,
  Campaign,
  CloudDownload,
  Devices,
  EmailOutlined,
  Logout,
  Palette,
  PersonOutline,
  PhoneIphone,
  Shield,
  VerifiedUser,
  VisibilityOutlined,
} from "@mui/icons-material";
import { SectionHeader } from "../../common/WorkspaceChrome";
import { CalendarProviderLogo } from "../../common/CalendarProviderLogo";
import { disconnectWorkspaceCalendar, exportWorkspaceAccountData, getWorkspaceCalendarConnections, startWorkspaceCalendarConnection, updateWorkspaceAccountPreferences } from "../../../../services/workspaceApi";

const accountDeleteRequestUrl =
  process.env.REACT_APP_MAIN_APP_URL ||
  (typeof window !== "undefined" && window.location.hostname.includes("localhost")
    ? "http://localhost:3000/account/delete-request"
    : "https://www.lyriclious.com/account/delete-request");

const calendarProviderItems = [
  {
    id: "google",
    label: "Google Calendar",
    helper: "Read-only access to your Google Calendar event titles and times.",
  },
  {
    id: "microsoft",
    label: "Outlook Calendar",
    helper: "Read-only access to your Outlook calendar event titles and times.",
  },
];

const formatCalendarConnectionTime = (value) => {
  if (!value) return "Not synced yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not synced yet";
  return parsed.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatShortDate = (value) => {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (value = "") =>
  `${value}`
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";

export function SettingsView({
  activeTab = "Account",
  themeMode = "default",
  appearanceMode = "light",
  setAppearanceMode = () => {},
  updateWorkspaceAppearancePreference = null,
  themeSaveStatus = "",
  canManagePersonalCalendars = false,
  currentUser = null,
  onAccountPreferencesUpdated = () => {},
  onOpenLogoutDialog = () => {},
  documentationMode = false,
}) {
  const changeAppearanceMode = (nextMode) => {
    if (documentationMode) return;
    if (updateWorkspaceAppearancePreference) {
      updateWorkspaceAppearancePreference(nextMode);
      return;
    }
    setAppearanceMode(nextMode);
  };
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [calendarProviders, setCalendarProviders] = useState({});
  const [calendarStatus, setCalendarStatus] = useState({ loading: false, action: "", error: "" });
  const [accountPrefs, setAccountPrefs] = useState({
    privacy: {
      profile_discoverable: true,
      personalized_recommendations: true,
    },
    communications: {
      learning_updates: true,
      opportunity_alerts: true,
      product_updates: true,
      newsletter: false,
    },
    accessibility: {
      reduced_motion: false,
    },
  });
  const [accountPrefsStatus, setAccountPrefsStatus] = useState({ saving: false, message: "", error: "" });
  const [exportStatus, setExportStatus] = useState({ loading: false, error: "", message: "" });

  useEffect(() => {
    const nextPreferences = currentUser?.accountPreferences || currentUser?.account_preferences || {};
    setAccountPrefs({
      privacy: {
        profile_discoverable: nextPreferences?.privacy?.profile_discoverable ?? true,
        personalized_recommendations: nextPreferences?.privacy?.personalized_recommendations ?? true,
      },
      communications: {
        learning_updates: nextPreferences?.communications?.learning_updates ?? true,
        opportunity_alerts: nextPreferences?.communications?.opportunity_alerts ?? true,
        product_updates: nextPreferences?.communications?.product_updates ?? true,
        newsletter: nextPreferences?.communications?.newsletter ?? false,
      },
      accessibility: {
        reduced_motion: nextPreferences?.accessibility?.reduced_motion ?? false,
      },
    });
  }, [currentUser]);

  useEffect(() => {
    if (documentationMode || !canManagePersonalCalendars || activeTab !== "Account") return undefined;
    let mounted = true;
    setCalendarStatus((current) => ({ ...current, loading: true, error: "" }));
    getWorkspaceCalendarConnections()
      .then((response) => {
        if (!mounted) return;
        setCalendarConnections(response.connections || []);
        setCalendarProviders(response.configured || {});
      })
      .catch((error) => {
        if (mounted) setCalendarStatus((current) => ({ ...current, error: error?.response?.data?.message || "Calendar connections could not be loaded." }));
      })
      .finally(() => {
        if (mounted) setCalendarStatus((current) => ({ ...current, loading: false }));
      });
    return () => { mounted = false; };
  }, [activeTab, canManagePersonalCalendars, documentationMode]);

  const connectCalendar = async (provider) => {
    if (documentationMode) return;
    setCalendarStatus({ loading: false, action: provider, error: "" });
    try {
      const { authorizationUrl } = await startWorkspaceCalendarConnection(provider);
      window.location.assign(authorizationUrl);
    } catch (error) {
      setCalendarStatus({ loading: false, action: "", error: error?.response?.data?.message || `Could not connect ${provider}.` });
    }
  };

  const disconnectCalendar = async (provider) => {
    if (documentationMode) return;
    const providerLabel = provider === "google" ? "Google Calendar" : "Outlook Calendar";
    if (!window.confirm(`Disconnect ${providerLabel}? Workspace will stop reading this personal calendar. Your calendar events will not be changed.`)) return;
    setCalendarStatus({ loading: false, action: provider, error: "" });
    try {
      await disconnectWorkspaceCalendar(provider);
      setCalendarConnections((current) => current.filter((connection) => connection.provider !== provider));
      setCalendarStatus({ loading: false, action: "", error: "" });
    } catch (error) {
      setCalendarStatus({ loading: false, action: "", error: error?.response?.data?.message || `Could not disconnect ${providerLabel}.` });
    }
  };

  const updatePreference = (section, key, value) => {
    if (documentationMode) return;
    setAccountPrefs((current) => ({
      ...current,
      [section]: {
        ...(current[section] || {}),
        [key]: value,
      },
    }));
    setAccountPrefsStatus((current) => ({ ...current, message: "", error: "" }));
  };

  const saveAccountPreferences = async () => {
    if (documentationMode) return;
    setAccountPrefsStatus({ saving: true, message: "", error: "" });
    try {
      const data = await updateWorkspaceAccountPreferences(accountPrefs);
      onAccountPreferencesUpdated(data.preferences || accountPrefs);
      setAccountPrefsStatus({ saving: false, message: "Personal account preferences saved.", error: "" });
    } catch (error) {
      setAccountPrefsStatus({ saving: false, message: "", error: error?.response?.data?.message || "Could not save personal account preferences." });
    }
  };

  const exportAccountData = async () => {
    if (documentationMode) return;
    setExportStatus({ loading: true, error: "", message: "" });
    try {
      const blob = await exportWorkspaceAccountData();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `lyriclious-account-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
      setExportStatus({ loading: false, error: "", message: "Account export downloaded." });
    } catch (error) {
      setExportStatus({ loading: false, error: error?.response?.data?.message || "Could not export account data.", message: "" });
    }
  };

  const sessionSummary = typeof window !== "undefined"
    ? `${window.navigator.platform || "This device"} · ${Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time"}`
    : "This browser session";
  const lastPreferenceUpdate = currentUser?.accountPreferences?.updated_at || currentUser?.account_preferences?.updated_at || "";
  const formattedPreferenceUpdate = formatCalendarConnectionTime(lastPreferenceUpdate);
  const userDisplayName = currentUser?.name || currentUser?.user_name || "Workspace member";
  const userEmail = currentUser?.email || "Email unavailable";
  const userPhone = currentUser?.mobilenum || currentUser?.mobile_number || currentUser?.mobileNumber || currentUser?.phone || "";
  const userAvatar = currentUser?.avatar?.url || currentUser?.avatarUrl || "";
  const joinedOn = formatShortDate(currentUser?.user_created_on || currentUser?.createdAt);
  const lastActiveOn = formatCalendarConnectionTime(currentUser?.last_logged_in_on || currentUser?.lastLoggedInOn);
  const connectedCalendarCount = calendarConnections.reduce((count, item) => count + ((item.selectedCalendarIds || []).length || 1), 0);
  const enabledPreferenceCount = useMemo(() => (
    Object.values(accountPrefs).reduce(
      (count, section) => count + Object.values(section || {}).filter(Boolean).length,
      0,
    )
  ), [accountPrefs]);
  const accountPreferenceSections = [
    {
      key: "privacy",
      eyebrow: "Privacy and discovery",
      title: "Profile visibility",
      description: "Control whether your profile is discoverable and whether personal activity improves what Lyriclious shows to you.",
      icon: VisibilityOutlined,
      items: [
        ["profile_discoverable", "Profile discoverable", "Allow your profile to appear in searchable workspace views where your access already permits visibility."],
        ["personalized_recommendations", "Personalized recommendations", "Use your account activity to improve the recommendations you see inside Lyriclious and Workspace."],
      ],
    },
    {
      key: "communications",
      eyebrow: "Communication preferences",
      title: "What reaches you",
      description: "Choose which updates still come to your personal account while leaving workspace-level notifications unchanged.",
      icon: Campaign,
      items: [
        ["learning_updates", "Learning updates", "Receive updates about programs, training activity, and learner-facing improvements."],
        ["opportunity_alerts", "Opportunity alerts", "Receive job, internship, and hiring opportunity alerts related to your account context."],
        ["product_updates", "Product updates", "Receive important product changes, release notes, and service updates."],
        ["newsletter", "Newsletter", "Receive occasional editorial updates, announcements, and broader Lyriclious news."],
      ],
    },
    {
      key: "accessibility",
      eyebrow: "Accessibility",
      title: "Motion and comfort",
      description: "Keep this account calmer across supported Workspace surfaces without affecting anyone else in the client workspace.",
      icon: AutoAwesome,
      items: [
        ["reduced_motion", "Reduced motion", "Prefer calmer transitions and lower animation intensity where supported."],
      ],
    },
  ];
  const communicationPreferenceSection = accountPreferenceSections.find((section) => section.key === "communications");
  const privacyPreferenceSection = accountPreferenceSections.find((section) => section.key === "privacy");
  const accessibilityPreferenceSection = accountPreferenceSections.find((section) => section.key === "accessibility");
  const connectedProvidersCount = calendarConnections.filter((item) => item.status === "connected").length;

  const renderPreferenceCard = (section) => {
    if (!section) return null;
    const Icon = section.icon;
    return (
      <div className="settings-preference-group settings-preference-card" key={section.key}>
        <div className="settings-preference-group-head">
          <span><Icon fontSize="small" /></span>
          <div>
            <small>{section.eyebrow}</small>
            <strong>{section.title}</strong>
            <p>{section.description}</p>
          </div>
        </div>
        <div className="settings-preference-list">
          {section.items.map(([key, title, description]) => (
            <label className="settings-toggle-row" key={key}>
              <div>
                <b>{title}</b>
                <span>{description}</span>
              </div>
              <input checked={Boolean(accountPrefs[section.key]?.[key])} onChange={(event) => updatePreference(section.key, key, event.target.checked)} type="checkbox" />
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderPreferenceSaveRow = (helperText = "These controls save to your own Lyriclious account preferences.") => (
    <div className="settings-preference-actions">
      <div>
        {accountPrefsStatus.error ? <p className="settings-inline-error" role="alert">{accountPrefsStatus.error}</p> : null}
        {accountPrefsStatus.message ? <p className="settings-inline-success" role="status">{accountPrefsStatus.message}</p> : <small>{helperText}</small>}
      </div>
      <button className="primary-action" disabled={documentationMode || accountPrefsStatus.saving} onClick={saveAccountPreferences} type="button">
        {documentationMode ? "Documentation preview" : accountPrefsStatus.saving ? "Saving…" : "Save preferences"}
      </button>
    </div>
  );

  return (
    <fieldset className="settings-view" disabled={documentationMode}>
      {documentationMode ? <p className="workspace-dialog-success" role="note">Documentation preview — account, calendar, appearance, and security controls are disabled.</p> : null}
      {activeTab === "Account" ? (
        <>
        <section className="panel settings-account-overview-panel">
          <div className="settings-account-hero">
            <div className="settings-account-identity">
              <span className="settings-account-avatar">
                {userAvatar ? <img alt={userDisplayName} src={userAvatar} /> : <b>{getInitials(userDisplayName)}</b>}
              </span>
              <div className="settings-account-identity-copy">
                <span className="settings-account-kicker">Account overview</span>
                <h3>{userDisplayName}</h3>
                <p>{userEmail}</p>
                <div className="settings-account-chip-row">
                  <em>{currentUser?.workspaceRole || currentUser?.role || "Member"}</em>
                  <em>{appearanceMode.charAt(0).toUpperCase()}{appearanceMode.slice(1)} mode</em>
                  <em>{connectedProvidersCount} provider{connectedProvidersCount === 1 ? "" : "s"} connected</em>
                </div>
              </div>
            </div>
            <div className="settings-account-overview-grid">
              <article>
                <span>Joined</span>
                <strong>{joinedOn}</strong>
                <p>Lyriclious account creation date</p>
              </article>
              <article>
                <span>Last activity</span>
                <strong>{lastActiveOn}</strong>
                <p>Most recent sign-in tracked for this account</p>
              </article>
              <article>
                <span>Preference status</span>
                <strong>{accountPrefsStatus.error ? "Needs attention" : accountPrefsStatus.saving ? "Saving…" : accountPrefsStatus.message ? "Saved" : "Ready"}</strong>
                <p>{accountPrefsStatus.message ? accountPrefsStatus.message : `Last saved: ${formattedPreferenceUpdate}`}</p>
              </article>
            </div>
          </div>

          <div className="settings-account-facts-grid">
            {[
              ["Login email", userEmail, "Single sign-in identity used across Lyriclious and Workspace", EmailOutlined],
              ["Phone", userPhone || "Not added", "Used only where your account profile and permissions allow it", PhoneIphone],
              ["Session", sessionSummary, "Current browser and local timezone summary", Devices],
              ["Workspace role", currentUser?.workspaceRole || currentUser?.role || "Member", "Your access in this workspace right now", PersonOutline],
            ].map(([label, value, note, Icon]) => (
              <article key={label}>
                <span><Icon fontSize="small" /></span>
                <div>
                  <strong>{label}</strong>
                  <b>{value}</b>
                  <p>{note}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="settings-preference-summary-bar">
            <article>
              <strong>{enabledPreferenceCount}</strong>
              <span>preferences enabled</span>
            </article>
            <article>
              <strong>{connectedProvidersCount}</strong>
              <span>providers connected</span>
            </article>
            <article>
              <strong>{connectedCalendarCount}</strong>
              <span>calendar feeds visible</span>
            </article>
          </div>

          <div className="settings-account-actions">
            <article>
              <span className="settings-action-icon"><CloudDownload fontSize="small" /></span>
              <strong>Export account data</strong>
              <p>Download the personal data currently stored against this Lyriclious account in JSON format.</p>
              <button className="secondary-action" disabled={documentationMode || exportStatus.loading} onClick={exportAccountData} type="button">
                {exportStatus.loading ? "Preparing…" : "Download export"}
              </button>
              {exportStatus.error ? <small className="settings-inline-error">{exportStatus.error}</small> : null}
              {exportStatus.message ? <small className="settings-inline-success">{exportStatus.message}</small> : null}
            </article>
            <article>
              <span className="settings-action-icon"><Logout fontSize="small" /></span>
              <strong>Sign out of this browser</strong>
              <p>End both your Workspace session and the shared Lyriclious session on this browser.</p>
              <button className="secondary-action" disabled={documentationMode} onClick={() => { if (!documentationMode) onOpenLogoutDialog(); }} type="button">
                Open sign-out
              </button>
            </article>
            <article>
              <span className="settings-action-icon warning"><Shield fontSize="small" /></span>
              <strong>Account deletion request</strong>
              <p>Request deletion of your Lyriclious account and connected personal Workspace data.</p>
              {documentationMode ? <button className="secondary-action" disabled type="button">Unavailable in documentation</button> : <a className="secondary-action" href={accountDeleteRequestUrl} rel="noreferrer" target="_blank">Open request</a>}
            </article>
          </div>
        </section>

        {canManagePersonalCalendars ? (
        <section className="panel personal-calendar-connections">
          <SectionHeader
            eyebrow="Personal integrations"
            title="Calendar connections"
            text="Workspace reads your personal calendar event titles and times only to show them inside your connected Workspace account in Home → Calendar. This connection is read-only and never creates, edits, or deletes calendar events."
          />
          <div className="personal-calendar-summary-grid">
            <article>
              <strong>{calendarConnections.filter((item) => item.status === "connected").length}</strong>
              <span>connected providers</span>
            </article>
            <article>
              <strong>{calendarConnections.reduce((count, item) => count + ((item.selectedCalendarIds || []).length || 1), 0)}</strong>
              <span>calendar feeds visible</span>
            </article>
            <article>
              <strong>Read-only</strong>
              <span>events are displayed only and never edited by Workspace</span>
            </article>
          </div>
          <div className="personal-calendar-connection-list">
            {calendarProviderItems.map((provider) => {
              const connection = calendarConnections.find((item) => item.provider === provider.id && item.status === "connected");
              const actionInProgress = calendarStatus.action === provider.id;
              const configured = Boolean(calendarProviders[provider.id]);
              const selectedCalendarCount = (connection?.selectedCalendarIds || []).length;
              return (
                <article key={provider.id}>
                  <span className={connection ? "is-connected" : ""}><CalendarProviderLogo provider={provider.id} size={18} /></span>
                  <div className="personal-calendar-provider-copy">
                    <div className="personal-calendar-provider-heading">
                      <strong>{provider.label}</strong>
                      <em className={connection ? "good" : configured ? "neutral" : "disabled"}>{connection ? "Connected" : configured ? "Available" : "Not configured"}</em>
                    </div>
                    <p>{provider.helper}</p>
                    {connection ? (
                      <div className="personal-calendar-provider-meta">
                        <span>{connection.email ? `Account: ${connection.email}` : "Connected account ready"}</span>
                        <span>{selectedCalendarCount ? `${selectedCalendarCount} calendar${selectedCalendarCount === 1 ? "" : "s"} selected` : "Primary calendar selected"}</span>
                        <span>Last synced: {formatCalendarConnectionTime(connection.lastSyncedAt)}</span>
                      </div>
                    ) : (
                      <div className="personal-calendar-provider-meta">
                        <span>{configured ? "You can connect this provider for personal schedule visibility in Home → Calendar." : "This provider is not enabled for this workspace yet."}</span>
                        <span>{configured ? "Disconnecting later only removes Workspace access. It does not touch your original events." : "Ask your workspace admin to enable this integration."}</span>
                      </div>
                    )}
                    {connection?.lastError ? <small className="personal-calendar-provider-inline-error">Last sync note: {connection.lastError}</small> : null}
                  </div>
                  <div className="personal-calendar-provider-actions">
                    {connection ? <button className="secondary-action calendar-provider-action danger" disabled={actionInProgress} onClick={() => disconnectCalendar(provider.id)} type="button"><CalendarProviderLogo provider={provider.id} />{actionInProgress ? "Disconnecting…" : "Disconnect"}</button> : configured ? <button className="secondary-action calendar-provider-action" disabled={actionInProgress || calendarStatus.loading} onClick={() => connectCalendar(provider.id)} type="button"><CalendarProviderLogo provider={provider.id} />{actionInProgress ? "Connecting…" : "Connect"}</button> : null}
                    <small>{connection ? "Stops future reads from this provider only." : configured ? "Opens the provider consent screen." : "Unavailable in this workspace."}</small>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="personal-calendar-trust-note">
            <strong>What Workspace can see</strong>
            <ul>
              <li>Event titles and times needed for your personal calendar view.</li>
              <li>Only inside your connected Workspace account.</li>
              <li>Never create, edit, delete, or move events in the source calendar.</li>
            </ul>
          </div>
          {calendarStatus.error ? <p className="personal-calendar-connection-error" role="status">{calendarStatus.error}</p> : null}
        </section>
        ) : null}
        </>
      ) : null}

      {activeTab === "Profile" ? (
        <section className="split-layout settings-profile-layout">
          <div className="panel settings-profile-summary-panel">
            <SectionHeader
              eyebrow="Profile summary"
              title="How you appear inside Workspace"
              text="Review the personal details this account brings into Workspace. Editing the full profile can stay in the main Lyriclious profile flow, while visibility is controlled here."
            />
            <div className="settings-profile-hero">
              <div className="settings-profile-identity settings-profile-identity-wide">
                <span className="settings-account-avatar">
                  {userAvatar ? <img alt={userDisplayName} src={userAvatar} /> : <b>{getInitials(userDisplayName)}</b>}
                </span>
                <div className="settings-profile-identity-copy">
                  <span className="settings-account-kicker">Workspace profile</span>
                  <h3>{userDisplayName}</h3>
                  <p>{userEmail}</p>
                  <div className="settings-account-chip-row">
                    <em>{currentUser?.workspaceRole || currentUser?.role || "Member"}</em>
                    <em>{userPhone || "Phone not added"}</em>
                  </div>
                </div>
              </div>
              <div className="settings-profile-overview-grid">
                <article className="settings-profile-stat-card">
                  <span>Profile visibility</span>
                  <strong>{accountPrefs.privacy.profile_discoverable ? "Discoverable" : "Restricted"}</strong>
                  <p>Search and people discovery inside allowed workspace views</p>
                </article>
                <article className="settings-profile-stat-card">
                  <span>Primary contact</span>
                  <strong>{userPhone || "Not added"}</strong>
                  <p>Phone value currently available on this account</p>
                </article>
                <article className="settings-profile-stat-card">
                  <span>Member since</span>
                  <strong>{joinedOn}</strong>
                  <p>Lyriclious account start date</p>
                </article>
              </div>
            </div>
            <div className="settings-profile-footnotes">
              <article>
                <strong>What teammates can rely on</strong>
                <p>Your name, role context, and allowed profile visibility stay consistent across workspace views that already permit access.</p>
              </article>
              <article>
                <strong>What this tab controls</strong>
                <p>This section helps you review profile presence inside Workspace. It does not change workspace permissions or admin access.</p>
              </article>
            </div>
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Visibility"
              title="Profile discovery controls"
              text="These controls decide how your own profile can be surfaced across Workspace screens."
            />
            {renderPreferenceCard(privacyPreferenceSection)}
            {renderPreferenceSaveRow("Profile visibility changes save to your own account and do not affect workspace permissions.")}
          </div>
        </section>
      ) : null}

      {activeTab === "Preferences" ? (
        <section className="split-layout">
          <div className="panel documentation-appearance-panel">
            <SectionHeader
              eyebrow="Workspace usage"
              title="Daily experience preferences"
              text="Set how this account experiences Workspace on this browser and across saved account-level preference areas."
            />
            <div className="settings-preference-summary-bar">
              <article>
                <strong>{appearanceMode.charAt(0).toUpperCase()}{appearanceMode.slice(1)}</strong>
                <span>appearance mode</span>
              </article>
              <article>
                <strong>{themeMode === "organization" ? "Workspace" : "Default"}</strong>
                <span>admin color</span>
              </article>
              <article>
                <strong>{accountPrefs.accessibility.reduced_motion ? "Reduced" : "Standard"}</strong>
                <span>motion profile</span>
              </article>
            </div>
            <div className="theme-mode-control">
              <strong>Display mode</strong>
              <div className="segmented-control" aria-label="Workspace appearance">
                <button className={appearanceMode === "light" ? "active" : ""} onClick={() => changeAppearanceMode("light")} type="button">
                  Light
                </button>
                <button className={appearanceMode === "dark" ? "active" : ""} onClick={() => changeAppearanceMode("dark")} type="button">
                  Dark
                </button>
                <button className={appearanceMode === "system" ? "active" : ""} onClick={() => changeAppearanceMode("system")} type="button">
                  System
                </button>
              </div>
              {themeSaveStatus ? <small className="theme-save-status">{themeSaveStatus}</small> : null}
            </div>
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Accessibility"
              title="Motion and comfort"
              text="Keep the visual experience calmer for this account without changing anyone else’s workspace experience."
            />
            {renderPreferenceCard(accessibilityPreferenceSection)}
            {renderPreferenceSaveRow("Accessibility preferences are stored against your own Lyriclious account.")}
          </div>
        </section>
      ) : null}

      {activeTab === "Notifications" ? (
        <section className="split-layout">
          <div className="panel">
            <SectionHeader
              eyebrow="Notifications"
              title="Communication preferences"
              text="Choose which personal updates should reach you. This is where communication preferences belong."
            />
            <div className="settings-preference-summary-bar">
              <article>
                <strong>{Object.values(accountPrefs.communications || {}).filter(Boolean).length}</strong>
                <span>channels enabled</span>
              </article>
              <article>
                <strong>{accountPrefs.communications.newsletter ? "Subscribed" : "Not subscribed"}</strong>
                <span>newsletter state</span>
              </article>
              <article>
                <strong>{accountPrefs.communications.product_updates ? "On" : "Off"}</strong>
                <span>product updates</span>
              </article>
            </div>
            {renderPreferenceCard(communicationPreferenceSection)}
            {renderPreferenceSaveRow("Notification preferences save to your personal account and do not alter project-level or admin-level workflow notifications.")}
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Delivery context"
              title="What these notifications cover"
              text="This tab controls only personal communication preferences connected to your Lyriclious account."
            />
            <div className="settings-security-grid">
              {[
                ["Learning", "Programs, training activity, and learner updates", accountPrefs.communications.learning_updates ? "Enabled" : "Muted", Campaign, accountPrefs.communications.learning_updates ? "good" : "neutral"],
                ["Opportunities", "Jobs, internships, and related alerts", accountPrefs.communications.opportunity_alerts ? "Enabled" : "Muted", Campaign, accountPrefs.communications.opportunity_alerts ? "good" : "neutral"],
                ["Product", "Release notes and service changes", accountPrefs.communications.product_updates ? "Enabled" : "Muted", VerifiedUser, accountPrefs.communications.product_updates ? "good" : "neutral"],
                ["Newsletter", "Editorial and broader Lyriclious updates", accountPrefs.communications.newsletter ? "Subscribed" : "Not subscribed", EmailOutlined, accountPrefs.communications.newsletter ? "good" : "neutral"],
              ].map(([title, detail, status, Icon, tone]) => (
                <article key={title}>
                  <span className={`settings-security-icon ${tone}`}><Icon fontSize="small" /></span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                  <em className={tone}>{status}</em>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "Appearance" ? (
        <section className="split-layout">
          <div className="panel documentation-appearance-panel">
            <SectionHeader
              eyebrow="Appearance"
              title="Workspace visual system"
              text="Choose light, dark, or system mode for this account. Icons and color are managed from Administration."
            />
            <div className="theme-mode-control">
              <strong>Display mode</strong>
              <div className="segmented-control" aria-label="Workspace appearance">
                <button className={appearanceMode === "light" ? "active" : ""} onClick={() => changeAppearanceMode("light")} type="button">
                  Light
                </button>
                <button className={appearanceMode === "dark" ? "active" : ""} onClick={() => changeAppearanceMode("dark")} type="button">
                  Dark
                </button>
                <button className={appearanceMode === "system" ? "active" : ""} onClick={() => changeAppearanceMode("system")} type="button">
                  System
                </button>
              </div>
              {themeSaveStatus ? <small className="theme-save-status">{themeSaveStatus}</small> : null}
            </div>
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Preview"
              title="Current appearance state"
              text="A compact summary of how this account is currently rendering the workspace."
            />
            <div className="settings-security-grid">
              {[
                ["Appearance", `${appearanceMode.charAt(0).toUpperCase()}${appearanceMode.slice(1)} mode`, "Saved to account", Palette, "neutral"],
                ["Admin color", themeMode === "organization" ? "Workspace color" : "Lyriclious default", "Administration", Palette, "good"],
                ["Motion", accountPrefs.accessibility.reduced_motion ? "Reduced motion" : "Standard motion", "Per account", AutoAwesome, "neutral"],
                ["Role context", currentUser?.workspaceRole || currentUser?.role || "Member", "View unchanged", PersonOutline, "good"],
              ].map(([title, detail, status, Icon, tone]) => (
                <article key={title}>
                  <span className={`settings-security-icon ${tone}`}><Icon fontSize="small" /></span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                  <em className={tone}>{status}</em>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "Privacy" ? (
        <section className="split-layout">
          <div className="panel">
            <SectionHeader
              eyebrow="Privacy"
              title="Visibility and recommendation controls"
              text="Control how this account is surfaced inside Workspace and whether activity can inform recommendations."
            />
            {renderPreferenceCard(privacyPreferenceSection)}
            {renderPreferenceSaveRow("Privacy controls affect only your own account visibility and recommendation behavior.")}
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Data usage"
              title="How your account data is used here"
              text="A compact summary of what Workspace reads from your account and how it behaves with it."
            />
            <div className="settings-security-grid">
              {[
                ["Profile discovery", accountPrefs.privacy.profile_discoverable ? "Allowed in permitted views" : "Hidden from discovery views", accountPrefs.privacy.profile_discoverable ? "Enabled" : "Restricted", VisibilityOutlined, accountPrefs.privacy.profile_discoverable ? "good" : "neutral"],
                ["Recommendations", accountPrefs.privacy.personalized_recommendations ? "Personalized recommendations can use your activity" : "Recommendations stay less personalized", accountPrefs.privacy.personalized_recommendations ? "Enabled" : "Limited", AutoAwesome, accountPrefs.privacy.personalized_recommendations ? "good" : "neutral"],
                ["Calendar access", connectedProvidersCount ? `${connectedProvidersCount} provider connected` : "No provider connected", "Read-only", Shield, "good"],
                ["Exports and deletion", "Manual actions remain available from Account", "Available", CloudDownload, "neutral"],
              ].map(([title, detail, status, Icon, tone]) => (
                <article key={title}>
                  <span className={`settings-security-icon ${tone}`}><Icon fontSize="small" /></span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                  <em className={tone}>{status}</em>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "Sessions" ? (
        <section className="split-layout">
          <div className="panel">
            <SectionHeader
              eyebrow="Sessions"
              title="Personal access signals"
              text="Review your current session, account status, and access context. Company-wide security remains in Administration."
            />
            <div className="settings-security-grid">
              {[
                ["This session", sessionSummary, "Active now", Devices, "good"],
                ["Workspace identity", currentUser?.email || "Lyriclious SSO account", "Verified", VerifiedUser, "good"],
                ["Appearance preference", `${appearanceMode.charAt(0).toUpperCase()}${appearanceMode.slice(1)} mode · colors from Administration`, "Saved", Palette, "neutral"],
                ["Account preferences", "Privacy, notifications, accessibility", accountPrefsStatus.error ? "Needs attention" : accountPrefsStatus.saving ? "Saving" : accountPrefsStatus.message ? "Saved" : "Ready", Shield, accountPrefsStatus.error ? "attention" : accountPrefsStatus.saving ? "neutral" : "good"],
              ].map(([title, detail, status, Icon, tone]) => (
                <article key={title}>
                  <span className={`settings-security-icon ${tone}`}><Icon fontSize="small" /></span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                  <em className={tone}>{status}</em>
                </article>
              ))}
            </div>
          </div>
          <div className="panel">
            <SectionHeader
              eyebrow="Session actions"
              title="Quick account actions"
              text="Use these when you need to leave this browser or export data tied to this account."
            />
            <div className="settings-account-actions settings-account-actions-compact">
              <article>
                <span className="settings-action-icon"><Logout fontSize="small" /></span>
                <strong>Sign out</strong>
                <p>Close the shared Lyriclious and Workspace session in this browser.</p>
                <button className="secondary-action" disabled={documentationMode} onClick={() => { if (!documentationMode) onOpenLogoutDialog(); }} type="button">Open sign-out</button>
              </article>
              <article>
                <span className="settings-action-icon"><CloudDownload fontSize="small" /></span>
                <strong>Export data</strong>
                <p>Download the current account export JSON for this user.</p>
                <button className="secondary-action" disabled={documentationMode || exportStatus.loading} onClick={exportAccountData} type="button">
                  {exportStatus.loading ? "Preparing…" : "Download export"}
                </button>
                {exportStatus.error ? <small className="settings-inline-error">{exportStatus.error}</small> : null}
                {exportStatus.message ? <small className="settings-inline-success">{exportStatus.message}</small> : null}
              </article>
            </div>
          </div>
        </section>
      ) : null}
    </fieldset>
  );
}
